/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Fragment } from 'react';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { AccordionGroup, VisuallyHidden } from '@coursera/cds-core';

import {
  SkillAreasThresholdRequestTrackerProvider,
  useSkillAreasThresholdRequestTracker,
} from 'bundles/assess-common/context/SkillAreasThresholdRequestTracker';
import type { ComputedItemType } from 'bundles/assess-common/lib/ComputedItem';
import type { QueryStateSuccessFragment } from 'bundles/assignment-submission/data/api/assignment-submission-api/__generated__/AssignmentSubmission';
import { type Track } from 'bundles/naptimejs/resources/programCurriculums.v1';
import SkillProgressDetailedListItem from 'bundles/skills-common/shared/verified-skills/SkillProgressDetailedListItem';
import type { RenderRetryActionButton } from 'bundles/skills-common/shared/verified-skills/SkillsDialog';
import type { VerifiedSkillsEnterpriseBadgeCollectionFragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseBadgeCollectionsQuery';
import type { VerifiedSkillsEnterpriseProgramsV1Fragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseProgramForCourseQuery';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type { SkillPathwayCurriculumProductsFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillPathwayCurriculumProductsQuery';
import useGetSkillAreasWithUserData from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetSkillAreasWithUserData';
import type { TrackingData } from 'bundles/skills-common/shared/verified-skills/types/TrackingData';

import _t from 'i18n!nls/skills-common';

type Props = {
  skillAreaAttainments: SkillAttainmentSkillAreaAttainmentFragment[];
  focusedSkillAreaId?: string;

  // props to handle retrying assessment when entering from and assignment results page
  item?: ComputedItemType;
  submissionState?: QueryStateSuccessFragment;
  itemMetadata?: ItemMetadata;
  refetchAssignmentSubmissionData?: () => Promise<void>;
  trackingData?: TrackingData;

  renderRetryActionButton?: RenderRetryActionButton;

  // enrollment props for enrol cards
  enrollmentProps?: {
    programId: string;
    pathwayCollectionId?: string;
    programSlug?: string;
    thirdPartyOrganizationSlug?: string;
  };
  skillPathCurriculumProducts?: (SkillPathwayCurriculumProductsFragment | null)[];
  program?: VerifiedSkillsEnterpriseProgramsV1Fragment | null;
  enrolledBadgeCollection?: VerifiedSkillsEnterpriseBadgeCollectionFragment | null;
  skillPath?: Track;
  handleDialogOnClose?: () => void;
};

const styles = {
  accordionGroup: css`
    .cds-AccordionGroup-itemSpacing {
      margin-top: 0 !important;
    }
  `,
};

type SkillProgressDetailedListContentProps = {
  children: React.ReactNode;
};

const SkillProgressDetailedListContent = ({ children }: SkillProgressDetailedListContentProps) => {
  const { showProgressBars } = useSkillAreasThresholdRequestTracker();

  return (
    <Fragment>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true" data-testid="skill-progress-status">
        {showProgressBars ? _t('Skill progress loaded') : _t('Loading skill progress')}
      </VisuallyHidden>
      <div aria-busy={!showProgressBars}>{children}</div>
    </Fragment>
  );
};

const SkillProgressDetailedList = ({
  skillAreaAttainments,
  focusedSkillAreaId,
  item,
  submissionState,
  itemMetadata,
  refetchAssignmentSubmissionData,
  trackingData,
  renderRetryActionButton,
  enrollmentProps,
  skillPathCurriculumProducts,
  program,
  enrolledBadgeCollection,
  skillPath,
  handleDialogOnClose,
}: Props) => {
  const { loading: skillAreasWithUserDataLoading, skillAreasWithUserData } = useGetSkillAreasWithUserData({
    programId: enrollmentProps?.programId || program?.id || '',
  });

  if (skillAreaAttainments?.length === 0) {
    return null;
  }

  const getSkillAreaAttainmentUserData = (skillAreaId: string) => {
    return skillAreasWithUserData.find((skillArea) => skillArea.skillArea.id === skillAreaId);
  };

  return (
    <AccordionGroup spacing={8} css={styles.accordionGroup}>
      <SkillAreasThresholdRequestTrackerProvider>
        <SkillProgressDetailedListContent>
          {skillAreaAttainments.map((skillAreaAttainment) => (
            <SkillProgressDetailedListItem
              key={skillAreaAttainment.skillArea.id}
              skillAreaAttainment={skillAreaAttainment}
              focusedSkillAreaId={focusedSkillAreaId}
              skillPathCurriculumProducts={skillPathCurriculumProducts}
              program={program}
              enrolledBadgeCollection={enrolledBadgeCollection}
              skillPath={skillPath}
              item={item}
              submissionState={submissionState}
              itemMetadata={itemMetadata}
              refetchAssignmentSubmissionData={refetchAssignmentSubmissionData}
              trackingData={trackingData}
              renderRetryActionButton={renderRetryActionButton}
              enrollmentProps={enrollmentProps}
              skillAreaWithUserData={getSkillAreaAttainmentUserData(skillAreaAttainment.skillArea.id)}
              skillAreasWithUserDataLoading={skillAreasWithUserDataLoading}
              handleDialogOnClose={handleDialogOnClose}
            />
          ))}
        </SkillProgressDetailedListContent>
      </SkillAreasThresholdRequestTrackerProvider>
    </AccordionGroup>
  );
};

export default SkillProgressDetailedList;
